import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface DashboardStat {
  title: string;
  value: number;
  increase: string;
  iconType: 'book' | 'alert' | 'check' | 'users';
}

export interface CourseProgress {
  name: string;
  enrolled: number;
  total: number;
}

export interface Task {
  id: string;
  type: 'Assignment' | 'Review' | 'Approval';
  status: 'Pending' | 'Urgent';
  description: string;
  dueDate: string;
  action: string;
}

export interface LearningHoursData {
  week: string;
  hours: number;
}

export interface LearningProgress {
  totalHours: number;
  progressPercentage: number;
  trend: 'up' | 'down' | 'neutral';
  trendValue: number;
  currentMonth: string;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export type TaskActionType = 'approve' | 'reject' | 'remind';

export interface Organization {
  id: number;
  name: string;
  is_active: number;
  created_at: string;
  assigned_at: string;
}

export interface OrganizationStat {
  title: string;
  value: number | string;
  iconType: string;
}

export interface OrganizationStats {
  organization: Organization | null;
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  usersByRole: Array<{ role_name: string; count: number }>;
  stats: OrganizationStat[];
}

export interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role_id: number;
  role_name: string;
  status: string;
  created_at: string;
  assigned_at: string;
  reportsToId?: string | null;
  reportsToName?: string | null;
}

export interface UsersPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface OrganizationUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  roleFilter?: number;
}

// ===== HELPER FUNCTIONS =====

const buildQueryParams = (dateRange?: DateRange, additionalParams?: Record<string, string | number>): string => {
  const params = new URLSearchParams();

  if (dateRange) {
    params.append('startDate', dateRange.startDate.toISOString().split('T')[0]);
    params.append('endDate', dateRange.endDate.toISOString().split('T')[0]);
  }

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.append(key, String(value));
    });
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

// ===== API Response Interface =====
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

// ===== Org Overview =====
export interface OrgOverview {
  scope: {
    orgIds: number[];
    organizationId: number | null;
    organizationName: string | null;
  };
  members: {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    newThisMonth: number;
  };
  teams: {
    totalTeams: number;
    withManager: number;
    withoutManager: number;
  };
  assignments: {
    total: number;
    active: number;
    upcoming: number;
    expired: number;
    pendingSubmissions: number;
  };
  recent: {
    assignments: Array<{
      id: number;
      uuid: string;
      title: string;
      type: 'document' | 'assessment';
      scope: 'organization' | 'team';
      start_date: string;
      end_date: string;
      created_at: string;
      submission_count: number;
    }>;
    submissions: Array<{
      id: number;
      uuid: string;
      assignment_id: number;
      assignment_uuid: string;
      assignment_title: string;
      user_id: string;
      user_email: string;
      user_name: string;
      submission_type: 'document' | 'assessment';
      status: string;
      submitted_at: string;
    }>;
  };
}

export const getOrgOverview = async (organizationId?: number | null): Promise<OrgOverview> => {
  const qs = organizationId ? `?organizationId=${organizationId}` : '';
  const response = await httpClient.get<ApiResponse<OrgOverview>>(
    `/admin/dashboard/org-overview${qs}`
  );
  return response.data;
};

// ===== SERVICE FUNCTIONS =====

export const getDashboardStats = async (date?: Date): Promise<{
  success: boolean;
  data?: DashboardStat[];
  error?: { message: string } | null;
}> => {
  try {
    let queryParams = '';
    if (date) {
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      queryParams = `?date=${dateStr}`;
    }

    const response = await httpClient.get<ApiResponse<DashboardStat[]>>(
      `/admin/dashboard/stats${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching stats:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch dashboard stats' }
    };
  }
};

export const calculateDailyStats = async (): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<any>>(
      `/admin/dashboard/stats/calculate`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error calculating stats:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to calculate dashboard stats' }
    };
  }
};

export const getTopCourses = async (dateRange?: DateRange, limit: number = 5): Promise<{
  success: boolean;
  data?: CourseProgress[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange, { limit });
    const response = await httpClient.get<ApiResponse<CourseProgress[]>>(
      `/admin/dashboard/top-courses${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching courses:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch courses' }
    };
  }
};

export const getTasks = async (dateRange?: DateRange, limit: number = 10): Promise<{
  success: boolean;
  data?: Task[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange, { limit });
    const response = await httpClient.get<ApiResponse<Task[]>>(
      `/admin/dashboard/tasks${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching tasks:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch tasks' }
    };
  }
};

export const getLearningHoursData = async (dateRange?: DateRange): Promise<{
  success: boolean;
  data?: LearningHoursData[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange);
    const response = await httpClient.get<ApiResponse<LearningHoursData[]>>(
      `/admin/dashboard/learning-hours${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching learning hours:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learning hours data' }
    };
  }
};

export const getLearningProgress = async (dateRange?: DateRange): Promise<{
  success: boolean;
  data?: LearningProgress;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange);
    const response = await httpClient.get<ApiResponse<LearningProgress>>(
      `/admin/dashboard/learning-progress${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching learning progress:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learning progress' }
    };
  }
};

export const performTaskAction = async (
  taskId: string,
  action: TaskActionType
): Promise<{
  success: boolean;
  data?: { message: string };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ message: string }>>(
      `/admin/dashboard/tasks/${taskId}/action`,
      { action }
    );

    return {
      success: response.success,
      data: { message: response.message || 'Action completed successfully' },
      error: null
    };
  } catch (error: any) {
    console.error(`AdminDashboardService: Error performing ${action} on task:`, error);
    return {
      success: false,
      error: { message: error?.message ?? `Failed to ${action} task` }
    };
  }
};

export const getTaskById = async (taskId: string): Promise<{
  success: boolean;
  data?: Task;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Task>>(
      `/admin/dashboard/tasks/${taskId}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching task:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch task' }
    };
  }
};

export const getAdminOrganizations = async (): Promise<{
  success: boolean;
  data?: Organization[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Organization[]>>(
      `/admin/dashboard/organizations`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching organizations:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch organizations' }
    };
  }
};

export const getOrganizationStats = async (organizationId: number): Promise<{
  success: boolean;
  data?: OrganizationStats;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<OrganizationStats>>(
      `/admin/dashboard/organization/${organizationId}/stats`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching organization stats:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch organization stats' }
    };
  }
};

export const getOrganizationUsers = async (
  organizationId: number,
  params?: OrganizationUsersParams
): Promise<{
  success: boolean;
  data?: User[];
  pagination?: UsersPagination;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.roleFilter) queryParams.append('roleFilter', params.roleFilter.toString());

    const queryString = queryParams.toString();
    const url = `/admin/dashboard/organization/${organizationId}/users${queryString ? `?${queryString}` : ''}`;

    const response = await httpClient.get<ApiResponse<User[]> & { pagination: UsersPagination }>(url);

    return {
      success: true,
      data: response.data,
      pagination: response.pagination,
      error: null
    };
  } catch (error: any) {
    console.error('AdminDashboardService: Error fetching organization users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch organization users' }
    };
  }
};
